"use client";

import { installAuthSession, fetchAuthWithConversation } from "@/lib/auth/client-session";
import { beginAuthTransition } from "@/lib/auth/transition";
import { waitForAnonymousSession } from "@/lib/auth/anonymous-session";
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import { useTurnstile } from "@/lib/turnstile/TurnstileProvider";
import { useOtpAutoSubmit } from "@/components/auth/useOtpAutoSubmit";
import { trackEvent } from "@/lib/analytics";
import { OTP_RESEND_COOLDOWN_SECONDS } from "@/lib/config";
import { useOtpDeliveryStatus, type OtpDeliveryAttempt } from "@/components/quick-flow/ergaenzen/useOtpDeliveryStatus";
export type EmailOtpEntityType = "request" | "offering";

/**
 * Cause of an OTP send attempt — emitted as a PostHog property on
 * `guest_otp_send_*` events so we can distinguish dead-button clicks
 * from auto-send misfires. See OBS-email-confirmation-button-recurring.
 */
export type OtpSendTrigger = "button" | "resend" | "auto" | "email_change" | "first_send" | "imperative";

/**
 * Defensive `data.error` extractor. `await response.json()` can resolve
 * to non-object values (string/array/null) or structured envelopes like
 * `{error: {field, message}}` — passing those straight to `setError`
 * would crash React. Returns the string-typed error or `null`.
 */
function extractServerErrorString(data: unknown): string | null {
  if (data !== null && typeof data === "object" && "error" in data && typeof (data as {
    error?: unknown;
  }).error === "string" && (data as {
    error: string;
  }).error.length > 0) {
    return (data as {
      error: string;
    }).error;
  }
  return null;
}
export interface UseEmailOtpOptions {
  entityId: string;
  entityType: EmailOtpEntityType;
  /** Pre-populated email; empty string means "user starts in edit mode". */
  initialEmail: string;
  /** Already-verified state; the consumer can short-circuit and render success. */
  initialVerified?: boolean;
  /** Called once verification succeeds. */
  onVerified?: () => void;
  /** Called the FIRST time an OTP is sent (resends don't re-trigger). */
  onOtpSent?: () => void;
}

/**
 * Shared state machine + API for email-OTP verification of an offering /
 * request entity. Used by both `OtpVerificationCard`
 * (standalone /verify page) and `ChatOtpCard` (inline in the chatbot).
 *
 * `code` is stored as a `string[6]` because `useOtpAutoSubmit` expects
 * that shape; consumers that want a single-input UX can join the array
 * (`code.join("")`) and call `setCode` with a freshly-built array.
 */
export function useEmailOtp(options: UseEmailOtpOptions) {
  const {
    entityId,
    entityType,
    initialEmail,
    initialVerified = false,
    onVerified,
    onOtpSent
  } = options;
  const {
    token: turnstileToken,
    resetToken,
    requestInteractiveToken,
    acquireToken
  } = useTurnstile();
  const [email, setEmail] = useState(initialEmail);
  const [isEditingEmail, setIsEditingEmail] = useState(!initialEmail);
  const [code, setCode] = useState<string[]>(Array(6).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [verified, setVerified] = useState(initialVerified);
  const [deliveryAttempt, setDeliveryAttempt] = useState<OtpDeliveryAttempt | null>(null);
  const delivery = useOtpDeliveryStatus(otpSent && !verified && !isEditingEmail && deliveryAttempt?.email === email ? deliveryAttempt : null);
  const inputsRef: MutableRefObject<(HTMLInputElement | null)[]> = useRef([]);

  // Synchronous-state shadows. React state updates are async, so
  // event handlers firing multiple times in one tick read stale
  // closure values. These mirror their state counterparts inline:
  //   - `inFlightRef`: double-click guard inside sendOtp
  //   - `otpSentRef`: read by sendOtp and submitEmail (first-send vs
  //     email-change discriminator) without stale closure
  //   - `hasAutoSentRef`: disarms the auto-send useEffect after ANY
  //     explicit send so a failed submit can't re-fire as "auto"
  const inFlightRef = useRef(false);
  const otpSentRef = useRef(false);
  const hasAutoSentRef = useRef(false);

  /** Use everywhere instead of bare `setOtpSent` to keep ref + state in sync. */
  const updateOtpSent = useCallback((next: boolean) => {
    otpSentRef.current = next;
    setOtpSent(next);
  }, []);

  // Default trigger is "button" so a bare `() => otp.sendOtp()`
  // tags correctly; pass an explicit trigger for the other paths.
  const sendOtp = useCallback(async (trigger: OtpSendTrigger = "button") => {
    // Synchronous double-click guard. `disabled={isSending}` only
    // catches up after React commits, so two clicks in one tick
    // could both pass the check and fire duplicate fetches.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    // Disarm the auto-send effect so a failure here doesn't get
    // immediately re-fired as `auto` once isSending flips back.
    hasAutoSentRef.current = true;
    // Sense the click BEFORE any state update or fetch — if we
    // see `_attempted` but no follow-up `_failed`/`_requested`,
    // the network call never resolved (the PR-#171 bisect lever).
    trackEvent("guest_otp_send_attempted", {
      entity_type: entityType,
      entity_id: entityId,
      trigger,
      has_email: Boolean(email),
      has_turnstile_token: Boolean(turnstileToken)
    });
    setIsSending(true);
    setError(null);
    setDeliveryAttempt(null);
    try {
      const post = (captchaToken: string | undefined) => fetch("/api/auth/email/send-verification-otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          entityId,
          entityType,
          captchaToken
        })
      });

      // The funnel spends the shared token on its anonymous sign-in and resets
      // the widget just before redirecting here, so the auto-send used to go
      // out ~0.4 s later without one (66 of 245 sends, 01.08.–02.11.2026). In
      // production a send without an accepted token goes out only once per
      // entity (ARCH-018), so wait for the next one.
      const firstToken = (await acquireToken()) ?? undefined;
      let sentWithToken = Boolean(firstToken);
      let response = await post(firstToken);

      // A 403 is the CAPTCHA gate, and a Turnstile token is SINGLE-USE:
      // once the server has redeemed it, Cloudflare answers
      // `timeout-or-duplicate` for every further attempt with the same
      // token. Reusing it (which this hook used to do — resetToken ran
      // only on success) makes the error unrecoverable: the user clicks
      // again, sends the same dead token, and gets the same message
      // forever, with no way out but a full page reload. Observed in
      // production on 2026-08-25.
      //
      // The token can also be spent BEFORE this page runs — the funnel
      // submit redeems the same shared context token and then redirects
      // straight to /verify — so this path is not exotic.
      //
      // Without a first token, acquireToken has already shown the visible
      // challenge and it was cancelled or failed; asking again at once would
      // only reopen the overlay the user just closed.
      if (response.status === 403 && firstToken) {
        resetToken();
        const fresh = await requestInteractiveToken().catch(() => null);
        if (fresh) {
          response = await post(fresh);
          sentWithToken = true;
        }
      }
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const serverErr = extractServerErrorString(data);
        // A 429 without our JSON comes from the edge's rate limit, which blocks
        // for about a minute; clicking again only extends it.
        setError(serverErr ?? (response.status === 429 ? "Zu viele Versuche. Bitte warten Sie eine Minute und versuchen Sie es dann erneut." : "Code konnte nicht gesendet werden"));
        trackEvent("guest_otp_send_failed", {
          entity_type: entityType,
          entity_id: entityId,
          trigger,
          status: response.status,
          reason: "non_2xx_response",
          error_message: serverErr ?? undefined,
          sent_with_token: sentWithToken
        });
        return;
      }
      resetToken();
      const data_0 = await response.json().catch(() => ({}));
      setDeliveryAttempt({
        receipt: typeof data_0?.deliveryReceipt === "string" ? data_0.deliveryReceipt : null,
        sentAt: Date.now(),
        email
      });
      // Snapshot BEFORE updateOtpSent — otherwise the ref check below
      // is always false (we just set it to true). Reading from the ref
      // (not closure `otpSent`) keeps a preceding submitEmail's
      // `updateOtpSent(false)` visible here.
      const wasFirstSend = !otpSentRef.current;
      updateOtpSent(true);
      setCooldown(Math.max(OTP_RESEND_COOLDOWN_SECONDS, Number(data_0?.resendAfterSeconds) || 0));
      if (wasFirstSend) onOtpSent?.();
      trackEvent("guest_otp_requested", {
        entity_type: entityType,
        entity_id: entityId,
        trigger,
        // Whether the request carried a token. `has_turnstile_token` on
        // `_attempted` only says one was ready at the click.
        sent_with_token: sentWithToken
      });
    } catch (err) {
      setError("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
      trackEvent("guest_otp_send_failed", {
        entity_type: entityType,
        entity_id: entityId,
        trigger,
        reason: "fetch_threw",
        error_message: err instanceof Error ? err.message : undefined
      });
    } finally {
      setIsSending(false);
      inFlightRef.current = false;
    }
  }, [email, entityId, entityType, turnstileToken, resetToken, requestInteractiveToken, acquireToken, onOtpSent, updateOtpSent]);

  // ── Verify OTP ──────────────────────────────────────────────────
  const verifyOtp = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const endAuthTransition = beginAuthTransition();
    try {
      await waitForAnonymousSession();
      const response_0 = await fetchAuthWithConversation("/api/auth/email/verify-verification-otp", {
        email,
        code: code.join(""),
        entityId,
        entityType
      });
      const data_1 = await response_0.json().catch(() => ({}));
      if (!response_0.ok) {
        // Same defensive extraction as sendOtp — see finding #5.
        setError(extractServerErrorString(data_1) ?? "Verifizierung fehlgeschlagen");
        setCode(Array(6).fill(""));
        setIsLoading(false);
        return;
      }
      const {
        createClient
      } = await import("@/lib/supabase/client");
      await installAuthSession(createClient(), data_1.session);
      setVerified(true);
      trackEvent("guest_otp_completed", {
        entity_type: entityType,
        entity_id: entityId
      });
      onVerified?.();
    } catch (error_0) {
      setError(error_0 instanceof Error ? error_0.message : "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      endAuthTransition();
      setIsLoading(false);
    }
  }, [email, code, entityId, entityType, onVerified]);

  // Auto-submit when all 6 digits are filled. Lives inside the hook so
  // both single-input and 6-box consumers get auto-submit for free.
  useOtpAutoSubmit({
    code,
    isReady: otpSent && !verified,
    isLoading,
    onSubmit: verifyOtp
  });

  // Cooldown ticker (1Hz, stops at 0).
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Auto-send on mount when we have what we need. `hasAutoSentRef`
  // is set by any explicit sendOtp call, so this can't re-fire as
  // `auto` after a failed user-initiated submit.
  useEffect(() => {
    if (hasAutoSentRef.current) return;
    if (initialVerified) return;
    if (otpSent) return;
    if (isEditingEmail) return;
    if (isSending) return;
    if (!email) return;
    hasAutoSentRef.current = true;
    sendOtp("auto");
  }, [email, isEditingEmail, isSending, otpSent, initialVerified, sendOtp]);

  // Called both for the initial email submit (ChatOtpCard with
  // empty initialEmail) and for "E-Mail-Adresse ändern" on /verify.
  // The otpSentRef discriminates: true means change, false means
  // first send.
  const submitEmail = useCallback(async () => {
    // If a previous send is still in flight, sendOtp's inFlightRef
    // guard would silently drop this call and the user's new email
    // would never reach the server — while the original send's
    // success UI shows the OLD email's "code sent" state. Refuse
    // BEFORE any state mutations so the editing view stays visible
    // with a clear error.
    if (inFlightRef.current) {
      setError("Bitte einen Moment warten — der vorherige Versand läuft noch.");
      return;
    }
    const isFirstSend = !otpSentRef.current;
    setIsEditingEmail(false);
    updateOtpSent(false);
    setCode(Array(6).fill(""));
    setError(null);
    await sendOtp(isFirstSend ? "first_send" : "email_change");
  }, [sendOtp, updateOtpSent]);
  const startEditingEmail = useCallback(() => {
    // A pending send still holds the old address. Keep editing unavailable until
    // it settles so its response cannot replace an edit with the code-entry view.
    if (inFlightRef.current) return;
    setDeliveryAttempt(null);
    setIsEditingEmail(true);
    updateOtpSent(false);
    setCode(Array(6).fill(""));
    setError(null);
  }, [updateOtpSent]);

  // Imperative trigger from /verify-page external buttons (header
  // status pill, footer "Code eingeben"): send if not yet sent,
  // otherwise focus the first empty digit.
  const requestCode = useCallback(() => {
    if (verified) return;
    if (!otpSent && email && !isSending) {
      sendOtp("imperative");
      return;
    }
    requestAnimationFrame(() => {
      const first = inputsRef.current.find(el => el && !el.value) ?? inputsRef.current[0];
      first?.focus();
    });
  }, [verified, otpSent, email, isSending, sendOtp]);
  return {
    // state
    email,
    isEditingEmail,
    code,
    isLoading,
    isSending,
    error,
    otpSent,
    cooldown,
    verified,
    deliveryStatus: delivery.status,
    showDeliveryRecovery: delivery.showRecovery,
    // refs (for the 6-box variant only)
    inputsRef,
    // setters
    setEmail,
    setIsEditingEmail,
    setCode,
    // actions
    sendOtp,
    verifyOtp,
    submitEmail,
    startEditingEmail,
    requestCode
  };
}
export type UseEmailOtpReturn = ReturnType<typeof useEmailOtp>;